import { useQuery } from '@tanstack/react-query'

import Client from '../../lib/client'
import isUnauthorized from '../../lib/isUnauthorized'

import getCacheKey from './getCacheKey'
import selectUserInfo from './selectUserInfo'
import shouldRetry from './shouldRetry'

const GET_URI = 'v1/users/info'

// A static value, so that consumers see the same `user` on every render
// while the request is in flight.
const DEFAULT_USER_INFO = {
  excluded: {},
  user: {},
}

// A refusal is an answer rather than a failure: the browser holds no credential
// the server will accept, which is the same thing it says about somebody who
// has never signed in. Reported as a guest so that consumers go on reading the
// one state they already handle, instead of each learning what a 401 is.
//
// Only ever substituted for a record that never arrived. A refusal that follows
// a record we already hold is a session that ended mid-visit, and answering
// that with a guest would quietly demote somebody in front of their own work.
const UNAUTHENTICATED_USER_INFO = {
  excluded: {},
  user: {
    guest: true,
  },
}

/**
 * @typedef  {object}   UserInfoQuery
 * @property {object}   excluded
 * @property {Function} getCacheKey
 * @property {boolean}  isUnauthorized - whether the server refused the
 *   browser's credential on a first fetch, in which case `user` reports a guest
 * @property {object}   user
 */

/**
 * @param {object} [options] - see "Options" at https://react-query.tanstack.com/reference/useQuery
 *
 * @returns {UserInfoQuery} also includes the response detailed at https://react-query.tanstack.com/reference/useQuery
 */
export default function useUserInfoQuery(options) {
  const {
    data = DEFAULT_USER_INFO,
    error,
    isLoadingError,
    ...otherReturnValues
  } = useQuery({
    queryFn: () => Client.get(GET_URI),
    queryKey: getCacheKey(),

    // The user record doesn't change while the app is open, so none of
    // react-query's automatic refetching buys us anything. Each refetch
    // costs a request, and a payload that differs from the cached one
    // hands every consumer a new `user` object, which re-runs anything
    // keyed on it.
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,

    // Both applications hold their whole tree back until this settles, so
    // every retry is spent in front of a spinner. Scoped here rather than on
    // the shared client, whose other queries run behind a page the person can
    // already see.
    retry: shouldRetry,

    // A second consumer mounting is not a reason to ask again, and React
    // Query answers the one that does with an optimistic `pending` carrying
    // no record at all. The consumers here read the record on their first
    // render, so that optimism is enough to have somebody treated as signed
    // in and sent away.
    retryOnMount: false,
    select: selectUserInfo,
    staleTime: Infinity,
    ...options,
  })

  const isUnauthorizedResponse = ( isLoadingError && isUnauthorized(error) )

  const {
    excluded,
    user,
  } = ( isUnauthorizedResponse ) ? UNAUTHENTICATED_USER_INFO : data

  return {
    ...otherReturnValues,
    error,
    excluded,
    getCacheKey,
    isLoadingError,
    isUnauthorized: isUnauthorizedResponse,
    user,
  }
}
